import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, Avatar, Divider, Chip } from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import BalanceIcon from '@mui/icons-material/Balance';
import PeopleIcon from '@mui/icons-material/People';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import { useLanguage } from '../../context/LanguageContext';

const GOLD = '#d4af37';

interface OfficialPerson {
  name: string;
  nameMalayalam?: string;
  role: string;
  roleMalayalam?: string;
  gradient: [string, string];
  imageUrl?: string;
}

// Kottayil Temple Officials
const kottayilTempleOfficials: OfficialPerson[] = [
  { name: 'Brahmasri Meppallimana Unnikrishnan Adithirippadu', role: 'Temple Thantri', gradient: ['#d4af37', '#b8941f'] },
  { name: 'Brahmasri Marakkattillath Unnikrishnan Namboothiri', role: 'Melsanthi', gradient: ['#e5c158', '#c9a227'] },
];

// Kovilakam Temple Officials
const kovilakamTempleOfficials: OfficialPerson[] = [
  {
    name: 'Brahmasri Mundottu Puliyapadambu Kuberan Somayajippadu',
    nameMalayalam: 'ബ്രഹ്മശ്രീ മുണ്ടോട്ട് പുളിയപടമ്പ് കുബേരൻ സോമയാജിപ്പാട്',
    role: 'Kshethram Thantri',
    gradient: ['#d4af37', '#b8941f'],
  },
  { name: 'Brahmasri Edamana Unnikrishnan Namboodiri', role: 'Melsanthi', gradient: ['#e5c158', '#c9a227'] },
];

// Temple Manager
const templeManager: OfficialPerson = {
  name: 'PK Somasekharan Vayangottu',
  nameMalayalam: 'പി.കെ. സോമശേഖരൻ വയങ്ങോട്ട്',
  role: 'Manager',
  gradient: ['#1e3a5f', '#2563eb'],
};

// Hereditary Trustees
const hereditaryTrustees: OfficialPerson[] = [
  { name: 'Mangoottil Asokan', nameMalayalam: 'മങ്കുട്ടിൽ അശോകൻ', role: 'Chairman', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Mangoottil Unni Nair Puthussery', nameMalayalam: 'മങ്കുട്ടിൽ ഉണ്ണി നായർ പുതുശ്ശേരി', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Mangoottil Venu Nair, Punathil', nameMalayalam: 'മങ്കുട്ടിൽ വേണു നായർ, പുനത്തിൽ', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Mangoottil Sasi, Elavana', nameMalayalam: 'മങ്കുട്ടിൽ ശശി, എളവന', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Mangoottil Ramachandran Nair', nameMalayalam: 'മങ്കുട്ടിൽ രാമചന്ദ്രൻ നായർ', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Madhu Mangoottil', nameMalayalam: 'മധു മങ്കുട്ടിൽ', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
  { name: 'Sanjay Marakkattu', role: 'Hereditary Trustee', gradient: ['#f59e0b', '#d97706'] },
];

// Non-Hereditary Trustees
const nonHereditaryTrustees: OfficialPerson[] = [
  { name: 'Prakasan Nellimadathil', role: 'Vice Chairman', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'Radhakrishnan T.C. Thazhe Cherukunnummal', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'Raghunath Othayoth', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'Damodaran Nair Pottakkattu', nameMalayalam: 'ദാമോദരൻ നായർ പൊറ്റക്കാട്ട്', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'Bijesh Ramanilayam', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'V.K Manojkumar', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
  { name: 'Rajeesh Edavalath', role: 'Non-Hereditary Trustee', gradient: ['#06b6d4', '#0891b2'] },
];

// Temple Welfare Committee
const welfareCommittee: OfficialPerson[] = [
  { name: 'Srinivasan Nair Kizhakkedath', role: 'President', gradient: ['#8b5cf6', '#7c3aed'] },
  { name: 'Karunan Kandiyil', role: 'Vice President', roleMalayalam: 'വൈസ്. പ്രസിഡണ്ട്', gradient: ['#6366f1', '#4f46e5'] },
  { name: 'Rajan KP Aswaradham', nameMalayalam: 'രാജൻ കെ.പി അശ്വരഥം', role: 'Secretary', gradient: ['#3b82f6', '#2563eb'] },
  { name: 'Ranjith MammiliMeethal', nameMalayalam: 'രഞ്ജിത്ത് മമ്മിളിമീത്തൽ', role: 'Joint Secretary', gradient: ['#0ea5e9', '#0284c7'] },
  { name: 'Babu Puthan Purayil', role: 'Treasurer', gradient: ['#22c55e', '#16a34a'] },
  { name: 'Gopalakrishnan Meethale Palayadi', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Asokan Padinjarayil', nameMalayalam: 'അശോകൻ പടിഞ്ഞാറയിൽ', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Arjun Madathil', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Narayanan Master Kizhakke Raroth', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Madhavan Nair Padikkalakkandi', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Sunilkumar Vipanchika', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Babu Thadathil', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'Sujith ValiyaValappil', role: 'Member', gradient: ['#64748b', '#475569'] },
  { name: 'K.V Sankaran', role: 'Member', gradient: ['#64748b', '#475569'] },
];

function PersonCard({ person, size = 'normal' }: { person: OfficialPerson; size?: 'featured' | 'normal' }) {
  const { language } = useLanguage();
  const isFeatured = size === 'featured';
  const displayName = person.nameMalayalam && language === 'ml' ? person.nameMalayalam : person.name;
  const displayRole = person.roleMalayalam && language === 'ml' ? person.roleMalayalam : person.role;
  const isMalayalamNameOverride = Boolean(person.nameMalayalam && language === 'ml');
  const isMalayalamRoleOverride = Boolean(person.roleMalayalam && language === 'ml');
  const initials = person.name.split(' ').map((w: string) => w.charAt(0)).slice(0, 2).join('').toUpperCase();
  const [gS, gE] = person.gradient;
  const hasImage = Boolean(person.imageUrl);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isFeatured ? 'center' : 'flex-start',
      gap: 1.5,
      p: isFeatured ? 3 : 2,
      borderRadius: isFeatured ? '22px' : '16px',
      bgcolor: isFeatured ? `${gS}06` : 'rgba(0,0,0,0.015)',
      border: `1px solid ${isFeatured ? `${gS}20` : 'rgba(0,0,0,0.04)'}`,
      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      textAlign: isFeatured ? 'center' : 'left',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 32px ${gS}15`,
        borderColor: `${gS}30`,
      },
    }}>
      <Avatar
        src={person.imageUrl || undefined}
        sx={{
        width: isFeatured ? 96 : 72,
        height: isFeatured ? 96 : 72,
        background: hasImage ? 'transparent' : `linear-gradient(135deg, ${gS}, ${gE})`,
        color: '#fff',
        fontWeight: 900,
        fontSize: isFeatured ? '1.5rem' : '1.1rem',
        border: `2px solid ${gS}25`,
        boxShadow: isFeatured ? `0 8px 24px ${gS}25` : `0 4px 12px ${gS}15`,
      }}>
        {!hasImage && <PersonIcon sx={{ fontSize: isFeatured ? '2.5rem' : '2rem' }} />}
      </Avatar>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Typography
          translate={isMalayalamNameOverride ? 'no' : undefined}
          sx={{
          fontWeight: 800,
          fontSize: isMalayalamNameOverride
            ? (isFeatured ? '1.02rem' : '0.9rem')
            : (isFeatured ? '1rem' : '0.84rem'),
          color: '#1e293b',
          lineHeight: isMalayalamNameOverride ? 1.4 : 1.3,
          mb: 0.4,
          fontFamily: isMalayalamNameOverride
            ? 'Noto Sans Malayalam, Nirmala UI, Kartika, sans-serif'
            : 'inherit',
          pl: isMalayalamNameOverride ? '0.08em' : 0,
          display: 'block',
        }}>
          {displayName}
        </Typography>
        <Box sx={{
          display: 'block',
          width: 'fit-content',
          px: isMalayalamRoleOverride ? 1.5 : 1.2,
          py: isMalayalamRoleOverride ? 0.45 : 0.3,
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${gS}12, ${gE}08)`,
          border: `1px solid ${gS}15`,
          overflow: 'visible',
          mt: 0.25,
          mx: isFeatured ? 'auto' : 0,
        }}>
          <Typography
            translate={isMalayalamRoleOverride ? 'no' : undefined}
            sx={{
            fontSize: isMalayalamRoleOverride
              ? (isFeatured ? '0.78rem' : '0.72rem')
              : (isFeatured ? '0.65rem' : '0.6rem'),
            fontWeight: isMalayalamRoleOverride ? 700 : 800,
            color: gS,
            textTransform: isMalayalamRoleOverride ? 'none' : 'uppercase',
            letterSpacing: isMalayalamRoleOverride ? 0 : 1,
            lineHeight: isMalayalamRoleOverride ? 1.35 : 1.2,
            fontFamily: isMalayalamRoleOverride
              ? 'Noto Sans Malayalam, Nirmala UI, Kartika, sans-serif'
              : 'inherit',
            pl: isMalayalamRoleOverride ? '0.18em' : 0,
            display: 'inline-block',
          }}>
            {displayRole}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, gradient }: { icon: React.ElementType; title: string; subtitle: string; gradient: [string, string] }) {
  const [gS, gE] = gradient;
  return (
    <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, mx: { xs: -2.5, sm: -3 }, mt: { xs: -2.5, sm: -3 }, mb: 2.5, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
          <Icon sx={{ fontSize: 22, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', mt: 0.2 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function Administration() {
  const [personImages, setPersonImages] = useState<Record<string, string>>({});

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'about_content', 'administration'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.personImages) setPersonImages(data.personImages);
      }
    });
    return () => unsub();
  }, []);

  const withImage = (person: OfficialPerson): OfficialPerson => ({
    ...person,
    imageUrl: personImages[person.name] || person.imageUrl,
  });

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Administration — Kottayil Temple" description="Administration team of Kottayil Temple (Muchukunnu Sri Kotta-Kovilakam Kshethram). Temple committee members, officials, and management." path="/about/administration" keywords="Kottayil temple administration, temple committee, Muchukunnu temple officials, temple management, കൊട്ടയിൽ ക്ഷേത്രം ഭരണസമിതി" />
      <PageBanner title="Administration" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 40%, #60a5fa 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <GroupsIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Temple Administration
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Governance guided by heritage, devotion & community service
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  A trustee board combining hereditary trustees, elected community representatives, and a court-appointed manager ensures both spiritual authenticity and transparent governance.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<BalanceIcon sx={{ fontSize: '16px !important', color: '#3b82f6 !important' }} />} label="Trustee Governed" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#1e3a5f', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<PeopleIcon sx={{ fontSize: '16px !important', color: '#93c5fd !important' }} />} label="Community Led" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* KOTTAYIL TEMPLE */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={TempleHinduIcon}
                  title="Kottayil Temple"
                  subtitle="Temple priests and spiritual leaders"
                  gradient={[GOLD, '#b8941f']}
                />
                <Grid container spacing={2}>
                  {kottayilTempleOfficials.map((person) => (
                    <Grid item xs={12} sm={6} key={person.name}>
                      <PersonCard person={withImage(person)} size="featured" />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* KOVILAKAM TEMPLE */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={TempleHinduIcon}
                  title="Kovilakam Temple"
                  subtitle="Kshethram priests"
                  gradient={['#8b5cf6', '#7c3aed']}
                />
                <Grid container spacing={2}>
                  {kovilakamTempleOfficials.map((person) => (
                    <Grid item xs={12} sm={6} key={person.name}>
                      <PersonCard person={withImage(person)} size="featured" />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* TEMPLE MANAGER */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={StarIcon}
                  title="Temple Manager"
                  subtitle="Administrative head overseeing temple operations"
                  gradient={['#1e3a5f', '#2563eb']}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    <PersonCard person={withImage(templeManager)} size="featured" />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* HEREDITARY TRUSTEES */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={GavelIcon}
                  title="Hereditary Trustees"
                  subtitle="Traditional custodians carrying forward ancestral stewardship"
                  gradient={['#f59e0b', '#d97706']}
                />
                <Box sx={{ mb: 2.5 }}>
                  <PersonCard person={withImage(hereditaryTrustees[0])} size="featured" />
                </Box>
                <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.04)' }} />
                <Grid container spacing={2}>
                  {hereditaryTrustees.slice(1).map((person) => (
                    <Grid item xs={12} sm={6} key={person.name}>
                      <PersonCard person={withImage(person)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* NON-HEREDITARY TRUSTEES */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={PersonIcon}
                  title="Non-Hereditary Trustees"
                  subtitle="Community-elected representatives serving the temple"
                  gradient={['#06b6d4', '#0891b2']}
                />
                <Box sx={{ mb: 2.5 }}>
                  <PersonCard person={withImage(nonHereditaryTrustees[0])} size="featured" />
                </Box>
                <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.04)' }} />
                <Grid container spacing={2}>
                  {nonHereditaryTrustees.slice(1).map((person) => (
                    <Grid item xs={12} sm={6} key={person.name}>
                      <PersonCard person={withImage(person)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* TEMPLE WELFARE COMMITTEE */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 2.5,
            }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader
                  icon={AccountBalanceIcon}
                  title="Temple Welfare Committee"
                  subtitle="Elected office bearers guiding welfare activities"
                  gradient={['#8b5cf6', '#7c3aed']}
                />
                <Box sx={{ mb: 2.5 }}>
                  <PersonCard person={withImage(welfareCommittee[0])} size="featured" />
                </Box>
                <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.04)' }} />
                <Grid container spacing={2}>
                  {welfareCommittee.slice(1).map((person) => (
                    <Grid item xs={12} sm={6} key={person.name}>
                      <PersonCard person={withImage(person)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* CLOSING DARK CARD */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 },
              position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />

              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <VolunteerActivismIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>A Legacy of Service</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Heritage governance for generations</Typography>
                  </Box>
                </Box>

                <Box sx={{ px: 2.5, py: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', mb: 2.5 }}>
                  <FormatQuoteIcon sx={{ fontSize: 24, color: `${GOLD}40`, transform: 'scaleX(-1)', mb: -0.5 }} />
                  <Typography sx={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.5)', lineHeight: 2, fontStyle: 'italic' }}>
                    From royal custodians to the modern trustee board, the temple administration has always been guided by devotion, transparency, and a deep commitment to preserving the sacred heritage of Sri Muchukunnu Kotta-Kovilakam Kshethram.
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {[
                    { label: 'Governance', value: 'Trustee Board', icon: AccountBalanceIcon, gradient: ['#8b5cf6', '#7c3aed'] },
                    { label: 'Court Oversight', value: 'Kerala High Court', icon: GavelIcon, gradient: ['#3b82f6', '#2563eb'] },
                    { label: 'Community Role', value: 'Since 1970', icon: GroupsIcon, gradient: ['#22c55e', '#16a34a'] },
                    { label: 'Reforms', value: 'HC Directive 2003', icon: BalanceIcon, gradient: ['#f59e0b', '#d97706'] },
                  ].map((fact, i) => {
                    const FIcon = fact.icon;
                    return (
                      <Grid item xs={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1.3, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: `linear-gradient(135deg, ${fact.gradient[0]}, ${fact.gradient[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 4px 12px ${fact.gradient[0]}25` }}>
                            <FIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.2 }}>{fact.label}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>{fact.value}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about/administration" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}